import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { supabaseClient } from "@/lib/supabase/client"
import { useToast } from "@/components/ui/ToastProvider"
import { IconMail, IconLock, IconEye, IconEyeOff, IconLoader } from "@/components/ui/Icons"

const schema = z.object({
  email: z.string().email("Valid email required"),
  password: z.string().min(1, "Password required"),
})
type FormData = z.infer<typeof schema>

export default function LoginPage() {
  const navigate = useNavigate()
  const { success, error: showError } = useToast()
  const [showPw, setShowPw] = useState(false)
  const [loading, setLoading] = useState(false)
  const { register, handleSubmit, setError, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  const onSubmit = async (data: FormData) => {
    setLoading(true)
    try {
      const { error } = await supabaseClient.auth.signInWithPassword({ email: data.email, password: data.password })
      if (error) {
        if (error.message.includes("Invalid login")) {
          setError("password", { message: "Incorrect email or password" })
        } else {
          showError("Sign in failed", error.message)
        }
        return
      }
      success("Signed in", "Welcome back!")
      navigate("/feed")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-[350px] space-y-3">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: "var(--ig-grad)" }}>
            <svg viewBox="0 0 48 48" fill="none" className="w-9 h-9">
              <path d="M15 17.5C15 14.462 17.462 12 20.5 12H29C30.105 12 31 12.895 31 14C31 15.105 30.105 16 29 16H20.5C19.672 16 19 16.672 19 17.5C19 18.328 19.672 19 20.5 19H27.5C30.538 19 33 21.462 33 24.5C33 27.538 30.538 30 27.5 30H19C17.895 30 17 30.895 17 32C17 33.105 17.895 34 19 34H28.5C29.328 34 30 34.672 30 35.5C30 36.328 29.328 37 28.5 37H19C16.239 37 14 34.761 14 32C14 29.239 16.239 27 19 27H27.5C28.328 27 29 26.328 29 25.5C29 24.672 28.328 24 27.5 24H20.5C17.462 24 15 21.538 15 18.5V17.5Z" fill="white" />
            </svg>
          </div>
          <h1 className="font-bold text-2xl tracking-tight">squids</h1>
        </div>

        {/* Form card */}
        <div className="auth-card">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
            <div>
              <div className="relative">
                <IconMail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input {...register("email")} type="email" placeholder="Email" className="input-ig pl-9" autoComplete="email" />
              </div>
              {errors.email && <p className="text-destructive text-xs mt-1">{errors.email.message}</p>}
            </div>
            <div>
              <div className="relative">
                <IconLock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input {...register("password")} type={showPw ? "text" : "password"} placeholder="Password" className="input-ig pl-9 pr-9" autoComplete="current-password" />
                <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted">
                  {showPw ? <IconEyeOff className="w-4 h-4" /> : <IconEye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && <p className="text-destructive text-xs mt-1">{errors.password.message}</p>}
            </div>
            <button type="submit" disabled={loading} className="btn-ig w-full py-2.5 mt-1">
              {loading ? <IconLoader className="w-4 h-4 animate-spin" /> : "Log in"}
            </button>
          </form>
          <div className="mt-3 text-center">
            <Link to="/auth/forgot-password" className="text-sm font-semibold text-brand hover:underline">Forgot password?</Link>
          </div>
        </div>

        {/* Register link */}
        <div className="auth-card text-center py-4">
          <span className="text-sm text-text-muted">Don't have an account? </span>
          <Link to="/auth/register" className="text-sm font-semibold text-brand">Sign up</Link>
        </div>
      </motion.div>
    </div>
  )
}